import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Tooltip } from "@msuite/ui/tooltip";
import { responseHandler } from "@msuite/ui/utils";
import type { IncomingEmailAttachmentListItem } from "@msuite/utils/types";
import { useState } from "react";
import { useNavigate } from "react-router";
import { client } from "@/hono";

/** Props Interface */
interface IncomingEmailAttachmentLinkProps {
	attachment: IncomingEmailAttachmentListItem;
}

export function IncomingEmailAttachmentLink({ attachment }: IncomingEmailAttachmentLinkProps) {
	/** Hooks */
	const { t } = useTranslation();
	const navigate = useNavigate();

	/** State */
	const [isResolving, setIsResolving] = useState(false);

	/** Functions */
	// The list names the file and nothing more; the signed link is asked for at the click, which
	// is what keeps a mailbox with years of mail cheap to list.
	async function handleOnOpen() {
		setIsResolving(true);
		try {
			const response = await client.files[":fileId"].$get({
				param: { fileId: attachment.fileId },
			});
			const data = await responseHandler(response);
			if (data.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
		} finally {
			setIsResolving(false);
		}
	}

	/** Render */
	return (
		<div className="h-stack gap-0">
			<Button
				size="xxs"
				variant="ghost"
				icon={<Icon name="Paperclip" />}
				onClick={handleOnOpen}
				isLoading={isResolving}
			>
				{attachment.originalFileName}
			</Button>
			{/* The invoice the pipeline read out of this attachment, once it has. */}
			{attachment.incomingInvoiceId && (
				<Tooltip label={t("incoming_invoices.open_invoice")}>
					<Button
						size="xxs"
						variant="ghost"
						icon={<Icon name="ReceiptText" />}
						onClick={() =>
							navigate(`/controlling/incoming-invoices/${attachment.incomingInvoiceId}`)
						}
					/>
				</Tooltip>
			)}
		</div>
	);
}
